import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import {AdtfDeviceOverviewContext} from "../context/AdtfDeviceOverviewContext";
import Alert from "@amzn/meridian/alert"
import {
    MAC_MASK,
    isValidEntry,
    IP_ADDRESS_PATTERN,
    PATTERNS, validateNewAdtfDevice, PROTOCOLS, VENDOR_INFO_KEYS,
    VENDOR_DISPLAY_VALUES, SPEC_VERSION_DISPLAY_VALUES, SORTER_TYPE_DISPLAY_VALUES
} from "../../../util/Validations";
import {assetTypeToConfigTypeDict, getSortedListWithOther} from "../../../util/Util";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {ADTF_DEFAULT_DEVICE_ID} from "../../../util/Constants";
import Select, {SelectOption} from "@amzn/meridian/select";
import {getLocalizedMessage} from "../../../util/Localization";

const AddAdtfSingleDeviceModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen, selected, setSelected} = props;
    const {showWarning} = useContext(GlobalStateContext);
    const {viewModel, dispatch} = useContext(AdtfDeviceOverviewContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false)

    // States
    const [port, setPort] = useState("");
    const [deviceType, setDeviceType] = useState(PROTOCOLS.ADTF[0]);
    const [name, setName] = useState("");
    const [ip, setIp] = useState("");
    const [mac, setMac] = useState("");
    const [id, setId] = useState(ADTF_DEFAULT_DEVICE_ID);
    const [vendor, setVendor] = useState("");
    const [specVersion, setSpecVersion] = useState("");
    const [sorterType, setSorterType] = useState("");

    // Error
    const [portErr, setPortErr] = useState(false)
    const [deviceTypeErr, setDeviceTypeErr] = useState(false)
    const [nameErr, setNameErr] = useState(false)
    const [vendorErr, setVendorErr] = useState(false);
    const [specVersionErr, setSpecVersionErr] = useState(false);
    const [sorterTypeErr, setSorterTypeErr] = useState(false);
    const [ipErr, setIpErr] = useState(false)
    const [macErr, setMacErr] = useState(false)
    const [idErr, setIdErr] = useState(false)
    const initialMonitoringValue = {
        startTime: "0",
        endTime: "0",
        reason: ""
    }
    const [monitoring, setMonitoring] = useState(JSON.stringify(initialMonitoringValue));
    const { showCustomAlert } = useContext(GlobalStateContext);

    const stateValues = {
        mac: mac,
        ip: ip,
        name: name,
        deviceType: PROTOCOLS.ADTF[0],
        port: port,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeADTF,
            id: id
        },
        vendorAttributes: {
            vendor: vendor,
            spec_version: specVersion,
            sorter_type: sorterType
        },
        monitoring: monitoring
    }
    const onCloseModal = () => {
        setOpen(false)
        setSelected("");
        resetInputState();
        setShowAlert(false);
    }
    const onClickConfirm = (stateValues) => {
        if (validateNewAdtfDevice(stateValues)) {
            showWarning(formatMessage({id: "device.editWarning"}), () => {
                setShowAlert(false)
                stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
                if (selected) {
                    stateValues.sorterConfig = JSON.stringify(viewModel.sorterConfig[stateValues.mac]);
                    stateValues.sorterTestPackage = JSON.stringify(viewModel.sorterTestPackageConfig[stateValues.mac]);
                    updateData(stateValues);
                } else {
                    stateValues.sorterConfig = "[]";
                    stateValues.sorterTestPackage = "[]";
                    addData(stateValues);
                }
                resetInputState();
                setOpen(false);
            });
        } else {
            setShowAlert(true)
        }
    }

    function resetInputState() {
        // setting state of input fields as empty to have empty inputs on clicking Add Device again
        setMac("");
        setMacErr(false);
        setMonitoring(JSON.stringify(initialMonitoringValue));
        resetAssetDetails();
        resetAssetDetailErrors();
    }

    const resetAssetDetails = () => {
        setIp("");
        setId(ADTF_DEFAULT_DEVICE_ID);
        setPort("");
        setDeviceType(PROTOCOLS.ADTF[0]);
        setName("");
        setVendor("");
        setSpecVersion("");
        setSorterType("");
    }

    const resetAssetDetailErrors = () => {
        setIpErr(false);
        setIdErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setNameErr(false);
        setVendorErr(false);
        setSpecVersionErr(false);
        setSorterTypeErr(false);
    }

    const prefill = (value) => {
        if (viewModel.adtfDeviceInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.adtfDeviceInformation[value];
            const vendorAttributes = device.vendorAttributes;
            resetAssetDetailErrors();
            setMac(device.mac)
            setIp(device.ip);
            setId(device.id);
            setPort(device.port);
            setDeviceType(device.deviceType);
            setName(device.name);
            setVendor(vendorAttributes.vendor);
            setSpecVersion(vendorAttributes.spec_version);
            setSorterType(vendorAttributes.sorter_type);
            const deviceMonitoring = {
                startTime: device.startTime,
                endTime: device.endTime,
                reason: device.reason
            }
            setMonitoring(JSON.stringify(deviceMonitoring));
        } else if (selected) {
            setSelected("");
            resetAssetDetails();
        }
    }

    const addData = (stateValues) => {
        dispatch({type: 'ADD_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues, showCustomAlert: showCustomAlert}})
    }
    const updateData = (stateValues) => {
        dispatch({type: 'UPDATE_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues, showCustomAlert: showCustomAlert}})
    }

    return (
        <Modal
            title={formatMessage({id: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        <FormattedMessage id="modal.mac" defaultMessage="MAC Address"/>
                    </Text>
                    <Input id="mac-input"
                        value={mac}
                        onChange={setMac}
                        mask={MAC_MASK}
                        disabled={selected}
                        onBlur={() => {
                            setMacErr(!isValidEntry(stateValues.mac, PATTERNS.MAC))
                            prefill(mac);
                        }}
                        error={macErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="ip-input">
                        <FormattedMessage id="modal.ip" defaultMessage="IP Address"/>
                    </Text>
                    <Input id="ip-input"
                        value={ip}
                        onChange={setIp}
                        onBlur={() => setIpErr(!isValidEntry(stateValues.ip, IP_ADDRESS_PATTERN))}
                        error={ipErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="port-input">
                        <FormattedMessage id="modal.port" defaultMessage="Port"/>
                    </Text>
                    <Input id="port-input"
                           value={port}
                           onChange={setPort}
                           onBlur={() => setPortErr(!isValidEntry(stateValues.port, PATTERNS.PORT))}
                           error={portErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        <FormattedMessage id="modal.protocol"
                                          defaultMessage="Protocol"/>
                    </Text>
                    <Input id="device-type-input"
                           value={deviceType}
                           error={deviceTypeErr}
                           disabled={true}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="name-input">
                        <FormattedMessage id="modal.name" defaultMessage="Device Name"/>
                    </Text>
                    <Input id="name-input"
                           value={name}
                           onChange={setName}
                           onBlur={() => setNameErr(!isValidEntry(stateValues.name, PATTERNS.NAME))}
                           error={nameErr}
                           helperText={getLocalizedMessage({intl: props.intl, stringId: 'modal.name_matrix_desc'})}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.device_id" defaultMessage="Device ID"/>
                    </Text>
                    <Input id="id-input"
                        value={id}
                        onChange={setId}
                        onBlur={() => setIdErr(!isValidEntry(stateValues.deviceConfig.id, PATTERNS.DEVICE_ID))}
                        error={idErr}
                        helperText={"Unique local identifier for the device. Default is 1"}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.vendor'})}
                    </Text>
                    <Select id="device-type-input"
                            value={vendor}
                            onChange={(value) => setVendor(value)}
                            onBlur={() => setVendorErr(!vendor)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_vendor'})}
                            error={vendorErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.VENDOR).map(value => <SelectOption key={value} value={value} label={VENDOR_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.spec_version'})}
                    </Text>
                    <Select id="device-type-input"
                            value={specVersion}
                            onChange={(value) => setSpecVersion(value)}
                            onBlur={() => setSpecVersionErr(!specVersion)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_version'})}
                            error={specVersionErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.SPEC_VERSION).map(value => <SelectOption key={value} value={value} label={SPEC_VERSION_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.sorter_type'})}
                    </Text>
                    <Select id="device-type-input"
                            value={sorterType}
                            onChange={(value) => setSorterType(value)}
                            onBlur={() => setSorterTypeErr(!sorterType)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_sorter_type'})}
                            error={sorterTypeErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.SORTER_TYPE).map(value => <SelectOption key={value} value={value} label={SORTER_TYPE_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.missingfields"
                                          defaultMessage="Please enter all fields in correct format!"/>
                    </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                    </Button>
                    <Button type="primary"
                            size="medium"
                            onClick={() => onClickConfirm(stateValues)}>
                        {selected ? <FormattedMessage id="modal.confirmEdit" defaultMessage="Confirm edit"/> :
                            <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(AddAdtfSingleDeviceModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtfdevice/modal/AddAdtfSingleDeviceModal.js